import { ACCOUNT_API, AccountUpdateRequest } from '@aloft/toolkit-shared';
import { UseMutationOptions, useMutation } from '@tanstack/react-query';

import { UpdateAccountError, fetchUpdateAccount } from '../../hooks/api/toolkitComponents.js';
import { ToolkitContext, useToolkitContext } from '../../hooks/api/toolkitContext.js';
import { apiAccountToToolkitAccount } from '../../utils/apiAccountToToolkitAccount.js';
import { ToolkitAccount } from '../types.js';

type ToolkitAccountUpdateVariables = Pick<
  ToolkitAccount,
  | 'address'
  | 'company'
  | 'firstName'
  | 'lastName'
  | 'mlsIDs'
  | 'phone'
  | 'userDefinedRole'
  | 'isArchived'
  | 'licenseID'
  | 'licenseState'
  | 'preferredReportSoftware'
  | 'isOnboardingComplete'
  | 'referredBy'
  | 'mcKissockData'
>;

/**
 * Updates an instance of `Account`.
 */
export const useUpdateToolkitAccount = (
  options?: Omit<
    UseMutationOptions<ToolkitAccount, UpdateAccountError, ToolkitAccountUpdateVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return useMutation<ToolkitAccount, UpdateAccountError, ToolkitAccountUpdateVariables>(
    (tkAccount) => {
      return updateAccount({ ...fetcherOptions, body: tkAccount });
    },
    {
      ...options,
    },
  );
};

const updateAccount = async (
  variables: { body: ToolkitAccountUpdateVariables } & ToolkitContext['fetcherOptions'],
  signal?: AbortSignal,
): Promise<ToolkitAccount> => {
  const { body } = variables;
  const request = toolkitAccountToAccountUpdateRequest(body);
  const validatedRequest = ACCOUNT_API.PUT.REQUEST.BODY.parse(request);
  const response = await fetchUpdateAccount(
    {
      ...variables,
      body: validatedRequest,
    },
    signal,
  );

  const validatedResponse = ACCOUNT_API.PUT.RESPONSE.parse(response);

  return apiAccountToToolkitAccount(validatedResponse);
};

function toolkitAccountToAccountUpdateRequest(
  tkAccount: ToolkitAccountUpdateVariables,
): AccountUpdateRequest {
  const {
    address,
    company,
    firstName,
    lastName,
    mlsIDs,
    phone,
    userDefinedRole,
    isArchived,
    licenseID,
    licenseState,
    preferredReportSoftware,
    isOnboardingComplete,
    referredBy,
    mcKissockData,
  } = tkAccount;
  return {
    isArchived,
    body: {
      version: 1,
      address,
      company,
      firstName,
      lastName,
      mlsIDs,
      phone,
      userDefinedRole,
      license: {
        id: licenseID,
        state: licenseState,
      },
      preferredReportSoftware,
      isOnboardingComplete,
      referredBy,
      mcKissockData,
    },
  };
}
